import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff, ArrowLeft } from 'lucide-react';

import AuthLayout from '../../shared/components/AuthLayout';
import Button from '../../shared/components/Button';
import Input from '../../shared/components/Input';
import LoginOption from '../../components/LoginOption';
import OverlayLoader from '../../components/Loader/OverlayLoader';
import loginImage from '../../assets/images/login.png';
import { useAuth } from '../../shared/hooks/useAuth';
import { useAuthForm } from '../../shared/hooks/useAuthForm';
import { useSocialAuth } from '../../shared/hooks/useSocialAuth';
import { AUTH_STACK } from '../../utils/screenNames';
import { loginWithEmailPhone } from '../../redux/actions/authActions';

/**
 * LoginWithEmail Page - Refactored with Tailwind CSS
 * Pure functional component using modern React patterns
 */
function LoginWithEmail() {
  const navigate = useNavigate();
  const { handleAuthSuccess } = useAuth();
  const {
    values,
    errors,
    handleChange,
    handleBlur,
    validateEmail,
    validatePassword,
    validate,
    setError,
  } = useAuthForm({ email: '', password: '' });
  const { signInWithProvider, isSocialLoading } = useSocialAuth((message) =>
    setError('email', message)
  );

  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  /**
   * Handle Email/Password Login
   */
  const handleLogin = async () => {
    // Validate form
    const isValid = validate({
      email: validateEmail,
      password: validatePassword,
    });

    if (!isValid) return;

    setIsLoading(true);

    try {
      await loginWithEmailPhone(
        values.email,
        null,
        values.password,
        (userData) => {
          handleAuthSuccess(userData, undefined, { loginMethod: 'email', isSignup: false });
        },
        (errMsg) => {
          setError('email', errMsg || 'Login failed');
          setIsLoading(false);
        }
      );
    } catch (error) {
      setError('email', 'An error occurred. Please try again.');
      setIsLoading(false);
    }
  };

  if (isLoading || isSocialLoading) {
    return <OverlayLoader />;
  }

  return (
    <div className="relative w-screen h-screen bg-bg-dark">
      {/* Back Button */}
      <button
        onClick={() => navigate(-1)}
        className="absolute top-6 left-6 z-20 flex items-center gap-2 text-shaded-text hover:text-[#FEFCEB] transition-colors"
      >
        <ArrowLeft className="w-5 h-5" />
        <span className="font-proxima text-sm">Back</span>
      </button>

      {/* Main Content */}
      <AuthLayout
        heroImage={loginImage}
        title="Log in to your account"
        subtitle="Enter your details to log in to your account."
      >
        {/* Email Input */}
        <div className="w-full">
          <label className="block text-[#FEFCEB] text-sm font-proxima mb-2">
            Email or user name
          </label>
          <Input
            type="email"
            name="email"
            placeholder="Email or user name"
            value={values.email}
            onChange={handleChange}
            onBlur={handleBlur}
            error={!!errors.email}
            errorMessage={errors.email}
          />
        </div>

        {/* Password Input */}
        <div className="w-full mt-4">
          <label className="block text-[#FEFCEB] text-sm font-proxima mb-2">
            Password
          </label>
          <Input
            type={showPassword ? 'text' : 'password'}
            name="password"
            placeholder="Password"
            value={values.password}
            onChange={handleChange}
            onBlur={handleBlur}
            error={!!errors.password}
            errorMessage={errors.password}
            rightIcon={
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="text-shaded-text hover:text-[#FEFCEB] transition-colors"
              >
                {showPassword ? (
                  <EyeOff className="w-5 h-5" />
                ) : (
                  <Eye className="w-5 h-5" />
                )}
              </button>
            }
          />
        </div>

        {/* Forgot Password Link */}
        <button
          onClick={() => navigate(AUTH_STACK.FORGOT_PASSWORD_WITH_EMAIL)}
          className="w-full text-left text-sm text-shaded-text hover:text-light-blue transition-colors font-proxima mt-2"
        >
          Forgot password?
        </button>

        {/* Login Button */}
        <Button
          variant="primary"
          size="lg"
          onClick={handleLogin}
          className="w-full mt-6"
        >
          Continue
        </Button>

        {/* Alternative login methods text */}
        <p className="w-full text-center text-sm text-shaded-text mt-4 font-proxima">
          Choose another login method
        </p>

        {/* Divider */}
        <div className="flex items-center gap-4 w-full my-6">
          <div className="flex-1 h-px bg-blue-shade-7" />
          <span className="text-shaded-text text-sm font-proxima">OR</span>
          <div className="flex-1 h-px bg-blue-shade-7" />
        </div>

        {/* Social Login Options */}
        <div className="w-full flex flex-col gap-3">
          <LoginOption
            optionType="google"
            optionText="Continue with Google"
            onOptionClick={() => signInWithProvider('google')}
          />
          <LoginOption
            optionType="apple"
            optionText="Continue with Apple"
            onOptionClick={() => signInWithProvider('apple')}
          />
        </div>

        {/* Sign Up Link */}
        <p className="text-center text-sm text-shaded-text mt-6 font-proxima">
          Don't have an account?{' '}
          <button
            onClick={() => navigate(AUTH_STACK.SIGNUP_OPTIONS)}
            className="text-[#FEFCEB] underline hover:text-light-blue transition-colors"
          >
            Register
          </button>
        </p>
      </AuthLayout>
    </div>
  );
}

export default LoginWithEmail;
